import { Box, Center, Spinner, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useSearchParams } from "react-router-dom";
import { SourcingEventStatus, SourcingEventDto, useListBidsForEventQuery } from "../../../autogen/bff-api";
import { ListLoadError } from "../../../common/ListLoadError";
import { NotReadyCard } from "../NotReadyCard";
import { eventIsDesignContest } from "../../sourcing-events/buyer/wizard/utils/designContest";
import { useDisplayDateWithTime } from "../../../common/formatting/useDisplayDateWithTime";
import { CompletedEvaluationStaleBanner } from "../evaluation/staleScore";
import { effectiveBidDeadline, isEvaluationLocked } from "../qualification/stage";
import { mustNegotiateBeforeAward, negotiatesUnderFoaPartIII } from "../rounds/partIIDialogue";
import { useRoundSelection } from "../rounds/RoundSelectionContext";
import { useCurrentMoment } from "../../sourcing-events/portal/utils/useCurrentMoment";
import { isBidDeadlinePassed } from "../supplierRows";
import { AwardOutcome } from "./AwardOutcome";
import { canSelectWinner, isBidDeadlineStillOpen, isEvaluationConcluded } from "./canSelectWinner";
import { hasStandingAwardDecision } from "./hasStandingAwardDecision";
import { ProductAwardSummary } from "./ProductAwardSummary";
import { RoundTendersScoredGate } from "./RoundTendersScoredGate";
import { SelectWinner } from "./SelectWinner";

export const AwardTab = ({ event }: { event: SourcingEventDto }) => {
  const { t } = useTranslation();
  const { rounds, roundsLoaded, roundsFailed, retryRounds } = useRoundSelection();
  const [searchParams] = useSearchParams();
  const displayDateWithTime = useDisplayDateWithTime();
  const now = useCurrentMoment().valueOf();
  const evaluationIsDone = canSelectWinner(event, rounds, now);
  const deadline = effectiveBidDeadline(event);
  const awaitingBidDeadline = isEvaluationConcluded(event.status, rounds) && isBidDeadlineStillOpen(event, now);
  const awaitingBidDeadlineCard = (
    <NotReadyCard
      title={String(t("The award is not ready yet"))}
      description={String(
        t(
          "The evaluation is complete, but the bid deadline {{date}} has not passed. Suppliers can still deliver or change their bids until then, and you can select a winner once it has passed.",
          { date: deadline ? displayDateWithTime(deadline) : "" },
        ),
      )}
    />
  );
  const toQualification = new URLSearchParams(searchParams);
  toQualification.set("tab", "qualification");
  const awaitingEvaluation = String(
    t("Awarding becomes available once the bid deadline has passed and the evaluation is completed."),
  );

  const inAwardableStatus =
    event.status === SourcingEventStatus.Evaluation || event.status === SourcingEventStatus.Evaluated;
  const mayHaveToNegotiate =
    inAwardableStatus &&
    !hasStandingAwardDecision(event) &&
    !isBidDeadlineStillOpen(event, now) &&
    negotiatesUnderFoaPartIII(event);
  const { data: bidsData, isLoading: bidsLoading } = useListBidsForEventQuery(
    mayHaveToNegotiate ? { eventId: event.id } : skipToken,
  );
  if (mayHaveToNegotiate && roundsFailed) {
    return <ListLoadError message={t("Could not load negotiation rounds.")} onRetry={retryRounds} />;
  }
  if (mayHaveToNegotiate && (!roundsLoaded || bidsLoading)) {
    return (
      <Center py="16">
        <Spinner color="smPrimary" />
      </Center>
    );
  }
  const panelOffersNoRound = event.status === SourcingEventStatus.Evaluation && bidsData?.bids.length === 0;
  if (mayHaveToNegotiate && mustNegotiateBeforeAward(event, rounds) && !panelOffersNoRound) {
    return (
      <NotReadyCard
        title={String(t("Negotiate before you award"))}
        description={String(
          t(
            "The notice did not reserve the right to award on the initial tenders (FOA § 23-7 (5)). Hold a negotiation round first. The Now panel at the top of the page shows the next step.",
          ),
        )}
      />
    );
  }

  switch (event.status) {
    // Picking a winner is the conclusion of the evaluation, not a shortcut past it: the scores and
    // the product basket are still moving while the event is being evaluated.
    case SourcingEventStatus.Evaluation:
      return (
        <Box>
          {awaitingBidDeadline ? (
            awaitingBidDeadlineCard
          ) : evaluationIsDone ? (
            <RoundTendersScoredGate event={event}>
              <SelectWinner event={event} />
            </RoundTendersScoredGate>
          ) : (
            <NotReadyCard
              title={String(t("The award is not ready yet"))}
              description={String(
                eventIsDesignContest(event)
                  ? t(
                      "Register the jury's decision before you select a winner. The Now panel at the top of the page offers the button once the whole jury has confirmed the report.",
                    )
                  : t(
                      "Complete the evaluation before you select a winner. The Now panel at the top of the page offers the button once every score is in.",
                    ),
              )}
            />
          )}
          <ProductAwardSummary event={event} />
        </Box>
      );
    case SourcingEventStatus.Evaluated:
      return (
        <Box>
          {hasStandingAwardDecision(event) ? (
            <AwardOutcome event={event} readOnly />
          ) : awaitingBidDeadline ? (
            awaitingBidDeadlineCard
          ) : (
            <Stack spacing="4">
              <CompletedEvaluationStaleBanner event={event} />
              <RoundTendersScoredGate event={event}>
              <SelectWinner event={event} />
            </RoundTendersScoredGate>
            </Stack>
          )}
          <ProductAwardSummary event={event} />
        </Box>
      );
    case SourcingEventStatus.Completed:
      return (
        <Box>
          {hasStandingAwardDecision(event) ? (
            <AwardOutcome event={event} readOnly />
          ) : (
            <NotReadyCard
              title={String(t("This procurement is completed"))}
              description={String(t("Reopen the procurement to evaluate the bids and select a winner."))}
            />
          )}
          <ProductAwardSummary event={event} />
        </Box>
      );
    case SourcingEventStatus.AwardDecision:
    case SourcingEventStatus.Awarded:
      return (
        <Box>
          <AwardOutcome event={event} />
          <ProductAwardSummary event={event} />
        </Box>
      );
    case SourcingEventStatus.Closed:
      return (
        <Box>
          <AwardOutcome event={event} readOnly />
          <ProductAwardSummary event={event} />
        </Box>
      );
    case SourcingEventStatus.Cancelled:
      return (
        <Center py="16">
          <Text color="smMuted">{t("This procurement has been cancelled")}</Text>
        </Center>
      );
    case SourcingEventStatus.Published:
      if (isEvaluationLocked(event)) {
        return (
          <NotReadyCard
            title={String(t("The award is not ready yet"))}
            description={String(
              t("The qualification must be completed before the bids can be evaluated and awarded."),
            )}
            action={{
              label: String(t("Go to the qualification review")),
              to: `?${toQualification}`,
            }}
          />
        );
      }
      return (
        <NotReadyCard
          title={String(t("The award is not ready yet"))}
          description={
            isBidDeadlinePassed(event)
              ? String(
                  t(
                    "The bid deadline has passed. The Now panel at the top of the page carries the next step, and the winner is picked once the evaluation is completed.",
                  ),
                )
              : awaitingEvaluation
          }
        />
      );
    default:
      return (
        <NotReadyCard title={String(t("The award is not ready yet"))} description={awaitingEvaluation} />
      );
  }
};
